import { Capacitor } from "@capacitor/core";
import i18next from "i18next";
import { toast } from "sonner";
import { Browser } from "@/lib/capacitor/browser";
import { logger } from "@/logger";

/** Only http(s) links may be opened; anything else is rejected. */
function isValidHttpUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

/**
 * Opens an external URL reliably on web and native (iOS/Android).
 *
 * Inside the Capacitor webview `window.open` is unreliable, so on native we use the Capacitor
 * Browser plugin (SFSafariViewController on iOS, Chrome Custom Tabs on Android). This also lets
 * links such as WhatsApp group invites hand off to the native WhatsApp app via universal/app
 * links.
 */
export async function openExternalUrl(url: string) {
  if (!isValidHttpUrl(url)) {
    logger.error("Refusing to open invalid url:", url);
    toast.error(i18next.t("common:error.open-link"));
    return;
  }

  try {
    if (Capacitor.isNativePlatform()) {
      await Browser.open({ url });
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  } catch (error) {
    logger.error("Failed to open external url:", error);
    toast.error(i18next.t("common:error.open-link"));
  }
}
